/*
 * CategoryTag.jsx
 * ---------------
 * Small colored pill label showing a checklist's category.
 * Supports built-in categories (Work, Personal, Home, Shopping)
 * and any custom category string.
 * Used on ChecklistCard, ChecklistDetail, and the Checklists filter bar.
 */

import React from "react";

// ============================================
// CATEGORY COLOR MAP
// ============================================
const CATEGORY_CONFIG = {
  Work: {
    bg: "var(--accent-green-muted)",
    color: "var(--accent-green)",
    icon: "💼"
  },
  Personal: {
    bg: "var(--accent-gold-muted)",
    color: "var(--accent-gold)",
    icon: "⭐"
  },
  Home: {
    bg: "#EEF2FF",
    color: "#4F46E5",
    icon: "🏠"
  },
  Shopping: {
    bg: "#FFF0F6",
    color: "#C2185B",
    icon: "🛒"
  },
  default: {
    bg: "var(--bg-surface-2)",
    color: "var(--text-muted)",
    icon: "📁"
  }
};

// Dark mode overrides for hardcoded colors
const DARK_CATEGORY_CONFIG = {
  Home: {
    bg: "#1E1B4B",
    color: "#818CF8",
    icon: "🏠"
  },
  Shopping: {
    bg: "#3B0764",
    color: "#E879F9",
    icon: "🛒"
  }
};

// ============================================
// MAIN EXPORT
// ============================================
export default function CategoryTag({
  category,
  size = "md",
  showIcon = true,
  onClick,
  active = false,
  theme = "light"
}) {
  if (!category) return null;

  const baseConfig = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.default;
  const darkOverride = DARK_CATEGORY_CONFIG[category];
  const config = theme === "dark" && darkOverride
    ? darkOverride
    : baseConfig;

  // ============================================
  // SIZE VARIANTS
  // ============================================
  const sizes = {
    sm: { fontSize: "11px", padding: "2px 8px", gap: "3px" },
    md: { fontSize: "12px", padding: "4px 10px", gap: "4px" },
    lg: { fontSize: "14px", padding: "6px 14px", gap: "6px" }
  };

  const sizeStyle = sizes[size] || sizes.md;

  return (
    <span
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: sizeStyle.gap,
        background: active ? config.color : config.bg,
        color: active ? "white" : config.color,
        fontSize: sizeStyle.fontSize,
        fontFamily: "var(--font-body)",
        fontWeight: 600,
        padding: sizeStyle.padding,
        borderRadius: "100px",
        border: `1px solid ${config.color}`,
        cursor: onClick ? "pointer" : "default",
        transition: "background var(--transition-fast), color var(--transition-fast)",
        whiteSpace: "nowrap",
        userSelect: "none",
        opacity: active ? 1 : 0.9
      }}
      onMouseEnter={e => {
        if (onClick) {
          e.currentTarget.style.background = config.color;
          e.currentTarget.style.color = "white";
        }
      }}
      onMouseLeave={e => {
        if (onClick) {
          e.currentTarget.style.background = active ? config.color : config.bg;
          e.currentTarget.style.color = active ? "white" : config.color;
        }
      }}
    >
      {showIcon && (
        <span style={{ fontSize: sizeStyle.fontSize }}>
          {config.icon}
        </span>
      )}
      {category}
    </span>
  );
}